import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ChevronDown, LogOut, Settings, UserRound } from "lucide-react";
import { Avatar } from "@/components/ui/avatar";
import { useAuthStore } from "@/stores/auth-store";
import { usePermission } from "@/hooks/use-permission";
import { cn } from "@/lib/utils";

const MENU_ITEMS = [
  { id: "account", label: "My Account", icon: UserRound },
  { id: "settings", label: "Settings", icon: Settings, href: "/settings", permission: "settings:read" as const },
];

export default function ProfileDropdown() {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const signOut = useAuthStore((s) => s.signOut);
  const user = useAuthStore((s) => s.user);
  const experience = useAuthStore((s) => s.experience);
  const canAccessSettings = usePermission("settings:read");

  const displayUser = user ?? { name: "User", email: "", initials: "U" };
  const activeOrg = experience.activeOrganization;

  const close = useCallback(() => setOpen(false), []);

  const items = useMemo(
    () => [
      ...MENU_ITEMS
        .filter((i) => !i.permission || (i.permission === "settings:read" && canAccessSettings))
        .map((i) => ({ ...i, action: () => i.href && navigate(i.href) })),
      { id: "signout", label: "Sign Out", icon: LogOut, action: () => { signOut().then(() => navigate("/login")); } },
    ],
    [navigate, signOut, canAccessSettings]
  );

  useEffect(() => {
    function onPointerDown(e: MouseEvent | TouchEvent) {
      if (!rootRef.current?.contains(e.target as Node)) close();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (!open) return;
      if (e.key === "Escape") {
        close();
        triggerRef.current?.focus();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % items.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + items.length) % items.length);
      } else if (e.key === "Enter" || e.key === " ") {
        if (activeIndex >= 0) items[activeIndex].action();
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, activeIndex, items, close]);

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`User menu · ${displayUser.name}`}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        className="flex items-center gap-2.5 rounded-md px-2 py-1 outline-none focus-visible:ring-2 focus-visible:ring-primary-soft hover:bg-bg"
      >
        <Avatar initials={displayUser.initials} size={32} bgColor="var(--ads-primary)" />
        <span className="hidden max-w-[190px] text-left leading-tight xl:block">
          <span className="block truncate text-base font-semibold leading-snug text-ink">{displayUser.name}</span>
          <span className="block max-w-[170px] truncate text-sm leading-snug text-muted">{displayUser.email}</span>
        </span>
        <ChevronDown aria-hidden className={cn("size-4 text-muted transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="User menu"
          className="absolute right-0 z-50 mt-2 w-72 overflow-hidden rounded-lg border border-line bg-surface shadow"
        >
          <div className="border-b border-line px-4 py-3.5">
            <div className="flex items-center gap-3">
              <Avatar initials={displayUser.initials} size={40} bgColor="var(--ads-primary)" />
              <div className="min-w-0 leading-tight">
                <div className="truncate text-sm font-semibold text-ink">{displayUser.name}</div>
                <div className="truncate text-xs text-muted">{displayUser.email}</div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 rounded-md bg-disabled-soft px-2.5 py-2 text-xs text-text">
              <Building2 aria-hidden className="size-3.5 shrink-0 text-muted" />
              <span className="truncate">{activeOrg.label}</span>
            </div>
            {activeOrg.roleSummary && (
              <div className="mt-2 rounded-md border border-line bg-surface-subtle px-2.5 py-2 text-xs text-muted">
                {activeOrg.roleSummary}
              </div>
            )}
          </div>
          <div className="p-1.5">
            {items.map((item, idx) => (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                onMouseEnter={() => setActiveIndex(idx)}
                onClick={item.action}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-sm px-3 py-2 text-left text-sm font-medium text-text outline-none focus-visible:ring-2 focus-visible:ring-primary-soft",
                  idx === activeIndex && "bg-primary-soft text-ink",
                  item.id === "signout" && "text-danger"
                )}
              >
                <item.icon aria-hidden className="size-4 shrink-0" />
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
